import {Button, Grid, Tabs, useTransition} from '@yandex-delivery/blocks'
import {assertNotExists} from '@yandex-delivery/core/utils/strict'
import {set} from '@yandex-delivery/utils'
import React, {memo} from 'react'

import {useController, useFormContext} from '../externalPackage'
import {fetchValues as _fetchValues} from './api'
import {ChangeHandlerFunction, Props, VerticalsValue} from './types'
import {Vertical} from './Vertical'

// TODO: в процессе переноса из newbiz-logistics-frontend возникли проблемы с импортом данного компонента из services/order/shared/components
// в данный момент заглушка, т.к. один из родительских компонентов нигде не используется
function Skeleton<P>(props: P) {
  return null
}

const defaultValue: VerticalsValue = {
  groupID: '',
  vertical: {},
}
const skeleton = (
  <Grid size="m" container>
    <Grid xs={12} item>
      <Skeleton height={6} />
    </Grid>
    <Grid xs={12} item>
      <Grid size="xs" container>
        {Array(4)
          .fill(null)
          .map((_, key) => (
            <Grid key={key} item xs>
              <Skeleton height={12.25} />
            </Grid>
          ))}
      </Grid>
    </Grid>
  </Grid>
)

export const Verticals = memo(function Verticals({name, fetchVerticals}: Props): React.ReactElement {
  const {control} = useFormContext()
  const {field} = useController({name, control, defaultValue})
  const value: VerticalsValue = field.value
  const changeHandler: ChangeHandlerFunction = function changeHandler(groupID, vertical) {
    let nextValue: VerticalsValue = value || {}

    // клик по вертикали (группу менять не надо)
    if (nextValue.groupID === groupID) {
      nextValue = set(`vertical.${groupID}`, vertical, nextValue)
    }
    // клик по группе "по городу"/"по стране"
    else {
      nextValue = set('groupID', groupID, nextValue)

      // выставляем вертикаль по дефолту, если ее не было
      if (nextValue.vertical?.[groupID] === undefined) {
        nextValue = set(`vertical.${groupID}`, vertical, nextValue)
      }
    }

    field.onChange(nextValue)
  }
  const fetchValues = _fetchValues(changeHandler, fetchVerticals)
  const [{payload, error}, startTransition] = useTransition(fetchValues, {key: `fetch/verticals/${name}`})
  const groupID = value.groupID

  if (payload && groupID) {
    const {verticals, groups} = payload
    const vertical = value.vertical?.[groupID]
    const groupVerticals = verticals[groupID]

    return (
      <Grid size="m" container>
        {groups.length > 1 ? (
          <Grid xs={12} item>
            <Tabs.Segments<string>
              items={groups}
              onChange={function groupChangeHandler(nextGroupID: string): void {
                if (nextGroupID !== groupID) {
                  const [nextVertical] = verticals[nextGroupID]

                  assertNotExists(nextVertical, `verticals not found for nextGroupID=${nextGroupID}`)
                  changeHandler(nextGroupID, nextVertical)
                }
              }}
              size="m"
              value={groupID}
            />
          </Grid>
        ) : null}
        {groupVerticals ? (
          <Grid xs={12} item>
            <Grid size="xs" container>
              {groupVerticals.map(nextVertical => (
                <Grid key={nextVertical.id} xs={3} item>
                  <Vertical
                    checked={nextVertical.id === vertical?.id}
                    onClick={function verticalChangeHandler(): void {
                      changeHandler(groupID, nextVertical)
                    }}
                    vertical={nextVertical}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        ) : null}
      </Grid>
    )
  }

  if (error) {
    assertNotExists(fetchValues, 'fetchValues not found')

    return (
      <Button
        onPress={function errorHandler() {
          startTransition(fetchValues)
        }}
        variant="error"
      >
        Повторить
      </Button>
    )
  }

  return skeleton
})
